import {
  availableWithin,
  AVAILABILITY_SLOTS,
  computeGaps,
  EXPERIENCE_TIER_LABEL,
  formatDuration,
  isCancelled,
  isExternal,
  MIN_SHIFT_MINUTES,
  staffs,
  minutesToHm,
  personFits,
  planAutofill,
  rankPiece,
  refusalText,
  shiftStanding,
  subtractRanges,
  unavailableSlots,
  wouldOverlap,
  type AutofillInput,
  type AutofillPlan,
  type AutofillProposal,
  type AutofillUnfilled,
  type AvailabilitySlot,
  type AvailabilityValue,
  type ConfirmationStatus,
  type MinuteRange,
  type PersonFit,
  type PieceCandidate,
  type ShiftPlanning,
} from '@reditelji/shared';
import type { FreedRefusalView, PoolVolunteer, Shift, TimelinePayload, TimelinePost } from '../api/types';
import type { DayPhase } from '../lib/ui';
import type { FilterChip } from '../state/ui';
import { refuses, standingWords, type VolunteerShift } from '../volunteers/derive';

export type BlockStatus =
  | 'potrjen'
  | 'caka'
  | 'zavrnjen'
  | 'opravljeno'
  | 'prosto'
  | 'predlog'
  | 'zunanja';

/**
 * Where one shift stands with its holder (SPEC §1.1F) — the only reading of it
 * in the SPA, so the board, the map, Predaje and the header cannot disagree,
 * whichever login looks: `shiftStatus` is given to all of them. The person's
 * status is never asked here — a shift nobody answered is still asked, and so
 * is a proposal, which was never on anybody's briefing.
 */
export function shiftConfirmation(shift: { id?: string }, holder: PoolVolunteer): ConfirmationStatus {
  // `standingWords`: while a deploy is under way this SPA can meet the API it
  // replaces, whose payload carries the answers and not where shifts stand.
  return shiftStanding(shift.id === undefined ? undefined : standingWords(holder)?.[shift.id]);
}

export function blockStatus(shift: Shift, volunteer: PoolVolunteer | undefined): BlockStatus {
  if (shift.state === 'done') return 'opravljeno';
  if (shift.state === 'proposed') return 'predlog';
  if (!shift.volunteerId || !volunteer) return 'prosto';
  // An external team is settled but not ours to chase, so it sits outside the
  // confirmation colours entirely (SPEC §1.1J).
  if (isExternal(volunteer)) return 'zunanja';
  // A shift somebody not coming kept is over or being worked: settled, with no
  // answer owed or possible (SPEC §1.1K).
  if (isCancelled(volunteer)) return 'potrjen';
  // The block says what was said about this shift (SPEC §1.1F).
  switch (shiftConfirmation(shift, volunteer)) {
    case 'potrjen':
      return 'potrjen';
    case 'zavrnjen':
      return 'zavrnjen';
    default:
      return 'caka';
  }
}

/**
 * Still owes us a confirmation. An external team never does (SPEC §1.1J), so it
 * is not a warning, not an urgent-post blocker and not a reminder count.
 */
export function awaitsConfirmation(
  shift: { id?: string; state: string },
  volunteer: PoolVolunteer | undefined,
): boolean {
  if (!volunteer || shift.state === 'done') return false;
  if (isExternal(volunteer)) return false;
  // Nor does somebody who is not coming (SPEC §1.1K).
  if (isCancelled(volunteer)) return false;
  return shiftConfirmation(shift, volunteer) !== 'potrjen';
}

/**
 * Which `.tl-status-dot--*` a person's dot wears. An external team's place has
 * no confirmation to be in (SPEC §1.1J), so it gets its own square instead.
 */
export function statusKey(v: PoolVolunteer): string {
  if (isExternal(v)) return 'zunanja';
  if (isCancelled(v)) return 'nepride';
  if (v.confirmationStatus === 'potrjen') return 'potrjen';
  if (v.confirmationStatus === 'zavrnjen') return 'zavrnjen';
  return 'caka';
}

/**
 * The dot for one shift's holder: what was said about *this* shift
 * (SPEC §1.1F), so a Friday refusal does not colour Sunday. A team and
 * somebody not coming keep their own marks.
 */
export function shiftStatusKey(shift: Shift, v: PoolVolunteer): string {
  if (isExternal(v) || isCancelled(v)) return statusKey(v);
  switch (blockStatus(shift, v)) {
    case 'potrjen':
    case 'opravljeno':
      return 'potrjen';
    case 'zavrnjen':
      return 'zavrnjen';
    default:
      return 'caka';
  }
}

/**
 * What a shift's holder is called on the board. A team place says who stands
 * there when the team named them (SPEC §1.1J): "PGD Bled 1 (Tine Brešan)".
 */
export function holderLabel(v: PoolVolunteer): string {
  return isExternal(v) && v.externalPersonName ? `${v.name} (${v.externalPersonName})` : v.name;
}

export function initials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0] ?? '')
    .join('')
    .toUpperCase();
}

export function range(fromMin: number, toMin: number): string {
  return `${minutesToHm(fromMin)}–${minutesToHm(toMin)}`;
}

/**
 * Does the post work on the payload's day? The timeline payload lists every
 * post with that day's windows and shifts, so one with neither is not open.
 */
export function postRunsOnDay(p: TimelinePost): boolean {
  return p.windows.length > 0 || p.shifts.length > 0;
}

/** Volunteer needs a (re)briefing: never sent, or sent for an older version. */
export function briefingStale(v: PoolVolunteer): boolean {
  // An external team has no briefing, so it can never have a stale one
  // (SPEC §1.1J). Without this every one of their blocks carries the ✉ mark,
  // the board's "brifing" filter catches them all, and the warnings panel asks
  // a coordinator to send instructions that are refused at the point of sending.
  if (isExternal(v)) return false;
  // Nor does somebody who is not coming: nothing is sent to them (SPEC §1.1K).
  if (isCancelled(v)) return false;
  // The mailed version, not the published one: a link handed over by SMS
  // publishes without writing, and reading that here would drop exactly the
  // cohort that still needs a napotnica (SPEC §1.1F).
  // Absent for a login without `comms:read` (SPEC §1.1L): no ✉ at all, not a ✉ on everyone.
  if (v.briefingMailedVersion === undefined) return false;
  return v.briefingMailedVersion === null || v.briefingMailedVersion < v.scheduleVersion;
}

/** Slots for one event day, split into morning (5h–12h) and afternoon/evening. */
function daySlots(dayKey: string) {
  const slots = AVAILABILITY_SLOTS.filter((s) => s.dayKey === dayKey);
  const isMorning = (s: (typeof slots)[number]) =>
    s.hours.startsWith('5h') || s.hours.startsWith('8h');
  return { morning: slots.filter(isMorning), rest: slots.filter((s) => !isMorning(s)) };
}

const canDo = (v: AvailabilityValue | undefined) => v === 'na-voljo' || v === 'delno';

/** Availability buckets for one day, from the signup-sheet slot data. */
export function availabilityBucket(
  availability: Record<string, AvailabilityValue>,
  dayKey: string,
): string {
  const { morning, rest } = daySlots(dayKey);
  const all = [...morning, ...rest];
  if (all.every((s) => availability[s.id] === undefined)) return 'Ni podatka';
  const m = morning.some((s) => canDo(availability[s.id]));
  const r = rest.some((s) => canDo(availability[s.id]));
  if (m && r) return 'Ves dan';
  if (m) return 'Dopoldne';
  if (r) return 'Popoldne in večer';
  return 'Ni na voljo';
}

export const BUCKET_ORDER = ['Ves dan', 'Dopoldne', 'Popoldne in večer', 'Ni na voljo', 'Ni podatka'];

/** Short human summary of one day's availability, e.g. "zjutraj, zvečer". */
export function availabilitySummary(
  availability: Record<string, AvailabilityValue>,
  dayKey: string,
): string {
  const slots = AVAILABILITY_SLOTS.filter((s) => s.dayKey === dayKey);
  const known = slots.filter((s) => availability[s.id] !== undefined);
  if (known.length === 0) return 'ni podatka o razpoložljivosti';
  const ok = slots.filter((s) => canDo(availability[s.id]));
  if (ok.length === 0) return 'ta dan ni na voljo';
  if (ok.length === slots.length) return 'na voljo ves dan';
  return ok.map((s) => `${s.name}${availability[s.id] === 'delno' ? ' (delno)' : ''}`).join(', ');
}

/**
 * The people this board may schedule.
 *
 * External teams ride in `data.volunteers` so the board can render and assign
 * them, but they are never *offered*: not to autofill, not as gap candidates,
 * not in the pool, and never counted as "free" (SPEC §1.1J). Every surface that
 * proposes somebody starts here rather than from `data.volunteers`.
 *
 * Somebody who is not coming (SPEC §1.1K) rides along for the same reason — a
 * shift they kept still needs a name — and is left out for the same one.
 */
export function rosterPool(data: TimelinePayload): PoolVolunteer[] {
  return data.volunteers.filter((v) => !isExternal(v) && !isCancelled(v));
}

/**
 * People who are not coming whose name matches a pool search (SPEC §1.1K).
 * Never offered: the pool lists them apart, so a coordinator looking for
 * somebody learns why the name is missing instead of finding nothing.
 */
export function cancelledMatching(data: TimelinePayload, query: string): PoolVolunteer[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  return data.volunteers.filter((v) => isCancelled(v) && v.name.toLowerCase().includes(q));
}

/** All of the day's shifts flattened from the payload. */
export function allDayShifts(data: TimelinePayload): Shift[] {
  return data.posts.flatMap((p) => p.shifts);
}

/**
 * What a run is asked to cover. Narrowing the scope drops posts from the
 * queue; the pool, everyone's load and the day's other shifts stay whole, so
 * a scoped run still respects overlaps, turnarounds and daily ceilings.
 */
export type PlanScope =
  | { kind: 'day' }
  | { kind: 'urgent' }
  | { kind: 'sector'; sectorId: string };

export const DAY_SCOPE: PlanScope = { kind: 'day' };

function inScope(p: TimelinePost, scope: PlanScope): boolean {
  switch (scope.kind) {
    case 'day':
      return true;
    case 'urgent':
      return p.urgent;
    case 'sector':
      return p.sectorId === scope.sectorId;
  }
}

/** The board payload as the shared autofill planner wants it. */
export function toAutofillInput(
  data: TimelinePayload,
  planning: ShiftPlanning,
  scope: PlanScope = DAY_SCOPE,
): AutofillInput {
  return {
    dayKey: data.day.key,
    planning,
    posts: data.posts
      .filter((p) => inScope(p, scope))
      .map((p) => ({
      id: p.id,
      code: p.code,
      type: p.type,
      urgent: p.urgent,
      gaps: p.gaps,
      shifts: p.shifts,
      openShifts: p.shifts
        .filter((sh) => sh.state === 'open' && sh.volunteerId === null)
        .map((sh) => ({ id: sh.id, fromMin: sh.fromMin, toMin: sh.toMin })),
      })),
    // External teams are never proposed for a gap (SPEC §1.1J) — they are not
    // ours to schedule. Their shifts stay in `shifts` below, so the time they
    // occupy still blocks overlaps and still counts against the day.
    volunteers: rosterPool(data).map((v) => ({
      id: v.id,
      name: v.name,
      availability: v.availability,
      declined: declinedOnDay(v, data),
      experience: v.experience,
      canMultiDay: v.canMultiDay,
      dayMinutes: v.dayMinutes,
      briefed: !briefingStale(v),
    })),
    shifts: allDayShifts(data),
  };
}

/** Plan the day's gaps, or a part of it (SPEC §4.3 heuristic; lives in `shared`). */
export function planDay(
  data: TimelinePayload,
  planning: ShiftPlanning,
  scope: PlanScope = DAY_SCOPE,
): AutofillPlan {
  return planAutofill(toAutofillInput(data, planning, scope));
}

/** Why a gap got nobody — the pool's own answers, not a shrug. */
export function unfilledReasonText(g: AutofillUnfilled): string {
  switch (g.reason) {
    case 'no-pool':
      return 'v bazi ni aktivnih prostovoljcev';
    case 'declined':
      return `vsi kandidati (${g.counts.declined}) so ta dan zavrnili izmeno`;
    case 'not-available':
      return 'nihče ni na voljo v tem terminu';
    case 'at-capacity':
      return `vsi razpoložljivi (${g.counts.atCapacity}) bi ta dan presegli dnevno omejitev`;
    case 'busy':
      return `vseh ${g.counts.available} razpoložljivih je takrat že zasedenih`;
  }
}

/**
 * Kaj je oseba o tem terminu povedala v prijavnici.
 *
 * Brez glagola: slovenski deležnik zahteva spol, tega pa v odzivu časovnice ni
 * in pri slabi tretjini prostovoljcev sploh ni vpisan. Samostalniška oblika je
 * pravilna za vse in vzporedna med vrsticami.
 */
const AVAILABILITY_NOTE: Record<AutofillProposal['availability'], string> = {
  yes: 'takrat na voljo',
  partial: 'na voljo, a ne v celoti',
  'no-data': 'za ta termin nimamo vprašanja v prijavnici',
  unknown: 'brez podatka o razpoložljivosti',
  no: 'takrat ni na voljo',
};

/** One-line justification shown on a proposal — availability first, then anything it bends. */
export function proposalReason(p: AutofillProposal): string {
  const parts = [AVAILABILITY_NOTE[p.availability]];
  for (const w of p.warnings) {
    parts.push(
      w.kind === 'short-gap'
        ? `⚠ le ${formatDuration(w.min)} premora do sosednje izmene`
        : `⚑ nujno mesto, ${EXPERIENCE_TIER_LABEL[w.tier]}`,
    );
  }
  return parts.join(' · ');
}

/**
 * The chips are a planning tool: they are hidden in live mode and on a finished
 * day. One spelling, so the toolbar that draws them and the board that obeys
 * them cannot disagree about when they apply.
 */
export function planningOn(live: boolean, phase: DayPhase): boolean {
  return !live && phase !== 'past';
}

/**
 * What a filter chip is about, at the level of one block.
 *
 * The chips used to narrow rows and nothing else, so a row kept alive by one
 * waiting izmena still showed every settled block and every PROSTO beside it.
 * The row rule is now derived from this one — a row survives iff something in
 * it matches — so the two can never drift, and each chip has a single
 * definition that the board, the badge and the row all read.
 *
 * `undefined` volunteer means the block is unstaffed, which is what `vrzeli`
 * is about.
 */
export function blockMatches(
  filters: ReadonlySet<FilterChip>,
  post: { urgent: boolean },
  shift: Shift,
  volunteer: PoolVolunteer | undefined,
): boolean {
  const gap = shift.state === 'open';
  const waiting = awaitsConfirmation(shift, volunteer);
  // The same test the ✉ mark on the block makes, so the chip and the mark
  // always agree about which blocks it is talking about.
  const unbriefed = volunteer !== undefined && shift.state !== 'done' && briefingStale(volunteer);
  return (
    (filters.has('vrzeli') && gap) ||
    (filters.has('caka') && waiting) ||
    (filters.has('brifing') && unbriefed) ||
    (filters.has('nujni') && post.urgent && (waiting || gap))
  );
}

/** A bare hole in the window: `vrzeli` is about it, and `nujni` on an urgent post. */
export function gapMatches(filters: ReadonlySet<FilterChip>, post: { urgent: boolean }): boolean {
  return filters.has('vrzeli') || (filters.has('nujni') && post.urgent);
}

/**
 * Does this row have anything left to show?
 *
 * A row the plan speaks about always does: plan review narrows the board to
 * exactly those rows, and a chip must not delete the plan out from under the
 * coordinator reviewing it.
 */
export function postMatches(
  filters: ReadonlySet<FilterChip>,
  post: TimelinePost,
  volunteerOf: (id: string) => PoolVolunteer | undefined,
  inPlan: boolean,
): boolean {
  if (inPlan) return true;
  if (post.gaps.length > 0 && gapMatches(filters, post)) return true;
  return post.shifts.some((s) =>
    blockMatches(filters, post, s, s.volunteerId ? volunteerOf(s.volunteerId) : undefined),
  );
}

/**
 * Slots this person closed that the gap runs into — the reason their row is
 * flagged, and what the confirmation names. Empty for anyone unaffected.
 */
export function gapMismatch(
  v: PoolVolunteer,
  gap: MinuteRange,
  dayKey: string,
): AvailabilitySlot[] {
  // A team carries no signup sheet at all, so it can never mismatch (SPEC §1.1J).
  return isExternal(v) ? [] : unavailableSlots(v.availability, dayKey, gap);
}

/**
 * "12h–20h" — the hours closed slots cover, in the sheet's own hour style,
 * slots that meet merged into one range. What a chip can hold where the slot
 * names ("zgodaj popoldne (12h–16h), pozno popoldne (16h–20h)") cannot.
 */
export function closedHours(slots: readonly MinuteRange[]): string {
  const hour = (m: number) => (m % 60 === 0 ? `${m / 60}h` : minutesToHm(m));
  const runs: MinuteRange[] = [];
  for (const s of [...slots].sort((a, b) => a.fromMin - b.fromMin)) {
    const last = runs.at(-1);
    if (last && last.toMin >= s.fromMin) last.toMin = Math.max(last.toMin, s.toMin);
    else runs.push({ fromMin: s.fromMin, toMin: s.toMin });
  }
  return runs.map((r) => `${hour(r.fromMin)}–${hour(r.toMin)}`).join(', ');
}

/** "zvečer (20h–24h)" — a closed slot named the way the signup sheet named it. */
export const slotLabel = (s: AvailabilitySlot) => `${s.name} (${s.hours})`;

export interface MismatchedShift {
  post: TimelinePost;
  shift: Shift;
  volunteer: PoolVolunteer;
  closed: AvailabilitySlot[];
}

/**
 * Every staffed, unfinished shift whose person closed a slot it runs into.
 *
 * One derivation for the board's ▲ marks and the Opozorila group, so the two
 * can never disagree about which shifts they are talking about. History and
 * cancellations are not warnings.
 */
export function mismatchedShifts(data: TimelinePayload): MismatchedShift[] {
  const byId = new Map(data.volunteers.map((v) => [v.id, v]));
  return data.posts.flatMap((post) =>
    post.shifts
      .filter((sh) => sh.volunteerId && sh.state !== 'done' && sh.state !== 'cancelled')
      .flatMap((shift) => {
        const volunteer = byId.get(shift.volunteerId!);
        if (!volunteer || isCancelled(volunteer)) return [];
        const closed = gapMismatch(volunteer, shift, data.day.key);
        return closed.length > 0 ? [{ post, shift, volunteer, closed }] : [];
      }),
  );
}

/**
 * Whether a row may take back `range`: no other row at the post that is not
 * called off — staffed or empty — stands in any of it, apart from `known`,
 * the rows already there when the hours were given away (an overlap
 * `resizeBounds` allows is not hours handed on). An undo that widens a
 * shortened shift asks this, so it never stacks over somebody placed meanwhile.
 */
export function rangeStillFree(
  shifts: readonly Shift[],
  shiftId: string,
  range: MinuteRange,
  known: ReadonlySet<string> = new Set(),
): boolean {
  return !shifts.some(
    (s) =>
      s.id !== shiftId &&
      !known.has(s.id) &&
      s.state !== 'cancelled' &&
      s.fromMin < range.toMin &&
      range.fromMin < s.toMin,
  );
}

export type AssignmentPlan =
  | { kind: 'staff'; shift: Shift; fromMin: number; toMin: number; narrows: boolean }
  | { kind: 'create'; fromMin: number; toMin: number };

/**
 * What putting somebody on `range` does to a post.
 *
 * A PROSTO slot the coordinator already cut is staffed in place rather than
 * duplicated (SPEC §3) — but only over the range actually asked for. The two
 * come apart whenever the assignment is *narrower* than the slot it lands in,
 * which is exactly what accepting an availability trim produces: staffing the
 * whole row there would hand the volunteer the very hours they said they could
 * not do. The slot narrows instead, and the remainder goes back to being an
 * uncovered gap that `computeGaps` re-derives for somebody else.
 */
export function assignmentPlan(
  post: { shifts: Shift[] },
  range: MinuteRange,
): AssignmentPlan {
  const slot = post.shifts.find(
    (s) => s.state === 'open' && s.fromMin <= range.fromMin && s.toMin >= range.toMin,
  );
  if (!slot) return { kind: 'create', fromMin: range.fromMin, toMin: range.toMin };
  return {
    kind: 'staff',
    shift: slot,
    fromMin: range.fromMin,
    toMin: range.toMin,
    narrows: slot.fromMin !== range.fromMin || slot.toMin !== range.toMin,
  };
}

/** A refusal on the board's day: a shift they hold, or one freed since (SPEC §1.1F). */
export interface DayRefusal {
  shiftId: string;
  postCode: string;
  fromMin: number;
  toMin: number;
  status: 'cant' | 'change';
  comment: string;
}

/** What a refusal of a shift freed since said about the board's day, if anything. */
const freedOnDay = (r: FreedRefusalView, data: TimelinePayload) => r.facts.find((x) => x.dayKey === data.day.key);


/**
 * What this person turned down or asked to change on the board's day
 * (SPEC §1.1F): on the shifts they hold, and on shifts freed since, which take
 * their answer off the board but not what it said about the day.
 * `skipShiftId` leaves out the holder's own answer on the shift being edited,
 * which is shown with it rather than raised as a reason to hesitate. It never
 * leaves out a freed refusal: nobody holds that shift, and offering it back to
 * the person who turned it down is exactly what has to ask. Held shifts are
 * read from where they stand, which every login is told; the words, and a
 * refusal of a shift freed since, only where the login is shown them (SPEC §1.1L).
 */
export function dayRefusals(v: PoolVolunteer, data: TimelinePayload, skipShiftId?: string): DayRefusal[] {
  const words = standingWords(v);
  const out: DayRefusal[] = [];
  for (const post of data.posts) {
    for (const shift of post.shifts) {
      if (shift.volunteerId !== v.id || shift.id === skipShiftId || shift.state !== 'assigned') continue;
      const status = words?.[shift.id];
      if (refuses(status)) {
        out.push({
          shiftId: shift.id,
          postCode: post.code,
          fromMin: shift.fromMin,
          toMin: shift.toMin,
          status,
          comment: v.shiftAnswers?.[shift.id]?.comment ?? '',
        });
      }
    }
  }
  for (const r of v.freedRefusals ?? []) {
    const f = freedOnDay(r, data);
    if (f) out.push({ shiftId: r.shiftId, postCode: f.postCode, fromMin: f.fromMin, toMin: f.toMin, status: r.status, comment: r.comment });
  }
  return out;
}

/** "ne more: GM3 · Ned 11:45–16:30" — a refusal named the way a coordinator finds it on the board. */
export function dayRefusalLabel(data: TimelinePayload, r: DayRefusal): string {
  return refusalText({ ...r, dayLabel: data.day.label });
}

/**
 * Whether placing this person on the board's day overrules something they
 * said: a shift they turned down *on this day*, held or freed since. A Friday
 * "ne more" is no reason to hesitate over Sunday — the person's status reads
 * "zavrnjen" for a refusal on any day, so it is never asked.
 */
export function declinedOnDay(v: PoolVolunteer, data: TimelinePayload, skipShiftId?: string): boolean {
  return dayRefusals(v, data, skipShiftId).length > 0;
}

export interface RefusedShift {
  post: TimelinePost;
  shift: Shift;
  volunteer: PoolVolunteer;
  /** The answer's words — `''` where none were given, or for a login not shown them (SPEC §1.1L). */
  comment: string;
}

/**
 * This day's shifts their holder turned down (`cant`) or asked to change
 * (SPEC §1.1F), each under its own id — what Opozorila lists and selects.
 *
 * A person's status reads "zavrnjen" for a refusal on any day, so asking it
 * here would list them on every day they work. Every login is told where each
 * shift stands, so the list agrees with the blocks beside it; the words ride
 * along only for a login shown them. `whole` is whoever reads "zavrnjen" with
 * no refused shift held any more.
 */
export function refusedShiftsOnDay(data: TimelinePayload): { cant: RefusedShift[]; change: RefusedShift[]; whole: PoolVolunteer[] } {
  const byId = new Map(data.volunteers.map((v) => [v.id, v]));
  const out = { cant: [] as RefusedShift[], change: [] as RefusedShift[], whole: [] as PoolVolunteer[] };
  const holdersToday = new Set<string>();
  for (const post of data.posts) {
    for (const shift of post.shifts) {
      if (shift.state !== 'assigned' || !shift.volunteerId) continue;
      const volunteer = byId.get(shift.volunteerId);
      if (!volunteer || isCancelled(volunteer) || isExternal(volunteer)) continue;
      holdersToday.add(volunteer.id);
      const status = standingWords(volunteer)?.[shift.id];
      const comment = volunteer.shiftAnswers?.[shift.id]?.comment ?? '';
      if (status === 'cant') out.cant.push({ post, shift, volunteer, comment });
      else if (status === 'change') out.change.push({ post, shift, volunteer, comment });
    }
  }
  // Reads "zavrnjen" as a person with no refused shift left in their hands
  // (`shiftStatus` names their assigned shifts on every day) — the refusal was
  // on a shift taken away or finished since — and works this day: the row
  // Pregled's Zavrnitve keeps for them, so the two lists agree.
  for (const id of holdersToday) {
    const v = byId.get(id)!;
    if (v.confirmationStatus === 'zavrnjen' && !Object.values(standingWords(v) ?? {}).some(refuses)) out.whole.push(v);
  }
  return out;
}

/**
 * One uncovered stretch a coordinator can fill: a bare gap, with any open row
 * inside it cut out, or an open row itself. The same pieces autofill works on
 * (`gapQueue`), before they are split — so a piece chosen inside one never runs
 * into an open row, and walking them never visits the same minutes twice.
 */
export type FillTarget =
  | { kind: 'gap'; postId: string; fromMin: number; toMin: number }
  | { kind: 'open'; postId: string; fromMin: number; toMin: number; shiftId: string };

const isOpenRow = (s: Shift) => s.state === 'open' && s.volunteerId === null;

/** The day's fill targets, urgent posts first, then by time, then by post code. */
export function fillTargets(data: TimelinePayload): FillTarget[] {
  const order = new Map(data.posts.map((p) => [p.id, p]));
  const out: FillTarget[] = [];
  for (const post of data.posts) {
    const open = post.shifts.filter(isOpenRow);
    for (const g of post.gaps) {
      for (const r of subtractRanges(g, open)) {
        // Every stretch the board draws as PROSTO, however short, is fillable here.
        out.push({ kind: 'gap', postId: post.id, fromMin: r.fromMin, toMin: r.toMin });
      }
    }
    for (const s of open) out.push({ kind: 'open', postId: post.id, fromMin: s.fromMin, toMin: s.toMin, shiftId: s.id });
  }
  const cmp = targetOrder(order);
  return out.sort(cmp);
}

function targetOrder(posts: Map<string, TimelinePost>) {
  return (a: { postId: string; fromMin: number }, b: { postId: string; fromMin: number }) => {
    const pa = posts.get(a.postId);
    const pb = posts.get(b.postId);
    return (
      Number(pb?.urgent ?? false) - Number(pa?.urgent ?? false) ||
      a.fromMin - b.fromMin ||
      (pa?.code ?? '').localeCompare(pb?.code ?? '', 'sl')
    );
  };
}

/**
 * The target a selection stands for. A selection comes from many places — a
 * gap segment on the board, which still spans the open rows inside it, an
 * Opozorila item, a plan's unfilled piece, a deep link — so it is matched to
 * the target holding its start, else the first it overlaps. `null` once
 * nothing there is uncovered any more.
 */
export function targetFor(
  data: TimelinePayload,
  sel: { postId: string; fromMin: number; toMin: number; shiftId?: string },
): FillTarget | null {
  const all = fillTargets(data);
  if (sel.shiftId) return all.find((t) => t.kind === 'open' && t.shiftId === sel.shiftId) ?? null;
  // A gap segment the board draws under an open row is that row: the row is
  // what there is to fill.
  const here = all.filter((t) => t.postId === sel.postId).sort((x, y) => x.fromMin - y.fromMin);
  return (
    here.find((t) => t.fromMin <= sel.fromMin && sel.fromMin < t.toMin) ??
    here.find((t) => t.fromMin < sel.toMin && sel.fromMin < t.toMin) ??
    null
  );
}

/**
 * A badge counts what its chip will leave standing on the board — blocks and
 * holes, one unit for all four. An uncovered stretch counts once as its fill
 * target: the gaps the timeline payload carries still span the open rows
 * inside them, so counting those gaps and the rows beside them would count
 * every PROSTO row twice.
 */
export function filterCounts(data: TimelinePayload): Record<FilterChip, number> {
  const volunteersById = new Map(data.volunteers.map((v) => [v.id, v]));
  const postsById = new Map(data.posts.map((p) => [p.id, p]));
  const targets = fillTargets(data);
  const counts = { nujni: 0, vrzeli: 0, caka: 0, brifing: 0 };
  for (const key of Object.keys(counts) as FilterChip[]) {
    const one: ReadonlySet<FilterChip> = new Set([key]);
    for (const t of targets) {
      const p = postsById.get(t.postId);
      if (p && gapMatches(one, p)) counts[key] += 1;
    }
    for (const p of data.posts) {
      for (const s of p.shifts) {
        // An open row is a fill target, counted above.
        if (isOpenRow(s)) continue;
        const v = s.volunteerId ? volunteersById.get(s.volunteerId) : undefined;
        if (blockMatches(one, p, s, v)) counts[key] += 1;
      }
    }
  }
  return counts;
}

/**
 * Where the piece editor starts. A selection narrower than its target is one
 * piece of it already chosen — a plan's leftover piece, a deep link — so it is
 * kept; a selection that is the whole target, or wider (a board gap segment
 * spanning open rows), starts from the piece autofill would cut.
 */
export function startingPiece(
  target: MinuteRange,
  sel: MinuteRange,
  suggested: MinuteRange,
): MinuteRange {
  const fromMin = Math.max(target.fromMin, sel.fromMin);
  const toMin = Math.min(target.toMin, sel.toMin);
  if (toMin - fromMin < 15 || (fromMin === target.fromMin && toMin === target.toMin)) return suggested;
  return { fromMin, toMin };
}

/**
 * The target N moves to: the one after the current selection in fill order,
 * wrapping round, among the targets `shown` keeps. With nothing selected, or a
 * selection that is no target any more, the first one after where it stood.
 */
export function nextTarget(
  data: TimelinePayload,
  current: { postId: string; fromMin: number; toMin: number; shiftId?: string } | null,
  shown: (t: FillTarget) => boolean = () => true,
): FillTarget | null {
  const list = fillTargets(data).filter(shown);
  if (list.length === 0) return null;
  if (!current) return list[0]!;
  const at = targetFor(data, current);
  const i = at ? list.findIndex((t) => t.postId === at.postId && t.fromMin === at.fromMin && t.kind === at.kind) : -1;
  if (i !== -1) return list[(i + 1) % list.length]!;
  const cmp = targetOrder(new Map(data.posts.map((p) => [p.id, p])));
  return list.find((t) => cmp(t, current) > 0) ?? list[0]!;
}

/** A ranked candidate, with the board's own row for the person. */
export interface RankedPerson extends PieceCandidate {
  person: PoolVolunteer;
}

export interface PieceCandidates {
  rec: RankedPerson[];
  warn: RankedPerson[];
  flag: RankedPerson[];
  busy: RankedPerson[];
  /**
   * Free places in an external team (SPEC §1.1J). A team covers a post like
   * anyone else, so a coordinator has to reach one, but it carries no
   * availability or answers to rank — it is its own group, never a candidate
   * beside people. Places already standing somewhere in the piece are left out.
   */
  teams: PoolVolunteer[];
}

/**
 * Who could stand at `piece`, by the autofill rules (`rankPiece`): the people
 * this board ranks (`rosterPool` — no team, nobody not coming), grouped and in
 * the planner's order, then the free team places.
 *
 * `planning` is the event's stored setting, not an autofill run's ceilings:
 * the list describes the day's rule, not one run's choice.
 */
export function pieceCandidates(
  data: TimelinePayload,
  planning: ShiftPlanning,
  piece: MinuteRange & { postId: string },
  opts: { holderId?: string } = {},
): PieceCandidates {
  const post = data.posts.find((p) => p.id === piece.postId);
  const byId = new Map(data.volunteers.map((v) => [v.id, v]));
  const ranked = rankPiece(
    toAutofillInput(data, planning),
    { ...piece, urgent: post?.urgent ?? false },
    { skipVolunteerId: opts.holderId },
  );
  const out: PieceCandidates = { rec: [], warn: [], flag: [], busy: [], teams: [] };
  for (const c of ranked) {
    const person = byId.get(c.volunteer.id);
    if (person) out[c.group].push({ ...c, person });
  }
  const shifts = allDayShifts(data);
  out.teams = data.volunteers
    .filter((v) => isExternal(v) && v.id !== opts.holderId && !wouldOverlap(v.id, piece, shifts))
    // The label carries the team and the place number, so a numeric collation
    // keeps each team together and its places in order: PGD Bled 2 before 10.
    .sort((a, b) => a.name.localeCompare(b.name, 'sl', { numeric: true }));
  return out;
}

export type ChipTone = 'good' | 'info' | 'warn' | 'flag' | 'mail';

/** What a candidate's row says about them, heaviest first. */
export function reasonChips(
  c: RankedPerson,
  data: TimelinePayload,
  planning: ShiftPlanning,
  silent: boolean,
): { tone: ChipTone; text: string }[] {
  const out: { tone: ChipTone; text: string }[] = [];
  for (const r of c.reasons) {
    switch (r.kind) {
      case 'declined':
        for (const d of dayRefusals(c.person, data)) out.push({ tone: 'flag', text: dayRefusalLabel(data, d) });
        break;
      case 'closed':
        // The verdict first: it is what the row is flagged for, and a chip cut
        // short by a narrow panel still says it.
        out.push({ tone: 'flag', text: `ni na voljo ${closedHours(r.slots)}` });
        break;
      case 'over-max':
        out.push({ tone: 'flag', text: `čez ${formatDuration(planning.maxDailyMin)} na dan` });
        break;
      case 'clips-closed':
        out.push({ tone: 'warn', text: 'se dotakne zaprtega termina' });
        break;
      case 'unknown':
        out.push({ tone: 'warn', text: 'ni podatka o razpoložljivosti' });
        break;
      case 'over-soft':
        out.push({ tone: 'warn', text: 'dolg dan' });
        break;
      case 'short-gap':
        out.push({ tone: 'warn', text: `le ${r.min} min premora` });
        break;
      case 'inexperienced-urgent':
        out.push({ tone: 'warn', text: r.tier === 'neznano' ? 'neocenjen na nujnem' : 'neizkušen na nujnem' });
        break;
      case 'available':
        out.push({ tone: 'good', text: 'na voljo' });
        break;
      case 'partial':
        out.push({ tone: 'info', text: 'delno na voljo' });
        break;
      case 'no-data':
        out.push({ tone: 'info', text: 'termina v prijavnici ni bilo' });
        break;
      case 'experienced-urgent':
        out.push({ tone: 'good', text: 'izkušen za nujno' });
        break;
    }
  }
  // A login not shown what was mailed (SPEC §1.1L) cannot tell, so says nothing.
  const mailed = c.person.briefingMailedVersion !== undefined;
  if (!silent && mailed && c.group !== 'busy' && !briefingStale(c.person)) {
    out.push({ tone: 'mail', text: '✉ znova potrjuje' });
  }
  return out;
}

/**
 * The pool card's dot for the board's day (SPEC §1.1F): what was said about the
 * shifts they hold here, so a Friday refusal does not colour Sunday. Somebody
 * with nothing here today reads by their status, without a refusal made
 * elsewhere. A refusal of a shift freed since counts for the login told of it
 * (SPEC §1.1L).
 */
export function dayStatusKey(v: PoolVolunteer, data: TimelinePayload): string {
  if (isExternal(v) || isCancelled(v)) return statusKey(v);
  const held = allDayShifts(data).filter((s) => s.volunteerId === v.id && s.state === 'assigned');
  const standing = held.map((s) => shiftConfirmation(s, v));
  if (standing.includes('zavrnjen') || (v.freedRefusals ?? []).some((r) => freedOnDay(r, data))) return 'zavrnjen';
  if (held.length === 0) return v.confirmationStatus === 'potrjen' ? 'potrjen' : 'caka';
  return standing.every((st) => st === 'potrjen') ? 'potrjen' : 'caka';
}

/**
 * The part of `range` this person did not close, longest first — or null when
 * there is nothing to trim: every touched slot closed, or none of them. A piece
 * that is the whole range is not a trim, so it is never offered: a button that
 * "trims" to the full range would read as the safe choice while overruling
 * somebody exactly as the orange one does.
 */
export function trimFor(
  availability: Record<string, AvailabilityValue>,
  dayKey: string,
  range: MinuteRange,
): MinuteRange | null {
  const pieces = availableWithin(availability, dayKey, range)
    .filter((r) => r.toMin - r.fromMin >= MIN_SHIFT_MINUTES)
    .sort((a, b) => b.toMin - b.fromMin - (a.toMin - a.fromMin));
  const best = pieces[0];
  if (!best || (best.fromMin === range.fromMin && best.toMin === range.toMin)) return null;
  return best;
}

export type BandValue = 'na-voljo' | 'delno' | 'ni-na-voljo' | 'neznano';

/**
 * A person's day as the signup sheet told it, one band per slot it asked
 * about — a blank or "neznano" answer as `neznano`. Hours the sheet never
 * asked about (Friday morning's 8h–12h, Sunday after 16h, the night) carry no
 * band at all: nothing was said there, which is not the same as a blank.
 */
export function availabilityBands(
  availability: Record<string, AvailabilityValue>,
  dayKey: string,
): (MinuteRange & { value: BandValue })[] {
  return AVAILABILITY_SLOTS.filter((s) => s.dayKey === dayKey)
    .sort((a, b) => a.fromMin - b.fromMin)
    .map((s) => ({ fromMin: s.fromMin, toMin: s.toMin, value: availability[s.id] ?? 'neznano' }));
}

export type DayShift = Shift & { postCode: string };

/** The rows a person holds on the board's day, in board order, with their post code. */
export function personDayShifts(data: TimelinePayload, volunteerId: string): DayShift[] {
  return data.posts.flatMap((p) =>
    p.shifts.filter((s) => s.volunteerId === volunteerId).map((s) => ({ ...s, postCode: p.code })),
  );
}

/** The row of theirs `range` would run into that day, other than `skipShiftId`. */
export function shiftClash(
  data: TimelinePayload,
  volunteerId: string,
  range: MinuteRange,
  skipShiftId?: string,
): DayShift | null {
  return (
    personDayShifts(data, volunteerId).find(
      (s) => s.id !== skipShiftId && s.fromMin < range.toMin && range.fromMin < s.toMin,
    ) ?? null
  );
}

/** Where the lens puts a person: a fit, with the board's own rows for it. */
export interface LensFit extends PersonFit<FillTarget> {
  post: TimelinePost;
  ranked: RankedPerson;
  /** The piece is narrower than its target, so the rest stays uncovered. */
  narrows: boolean;
}

/** Every uncovered stretch this person could take on the board's day (`personFits`). */
export function lensFits(data: TimelinePayload, planning: ShiftPlanning, volunteerId: string): LensFit[] {
  const person = data.volunteers.find((v) => v.id === volunteerId);
  if (!person) return [];
  const posts = new Map(data.posts.map((p) => [p.id, p]));
  return personFits(toAutofillInput(data, planning), volunteerId, fillTargets(data)).flatMap((f) => {
    const post = posts.get(f.target.postId);
    if (!post) return [];
    return [
      {
        ...f,
        post,
        ranked: { ...f.candidate, person },
        narrows: f.piece.fromMin !== f.target.fromMin || f.piece.toMin !== f.target.toMin,
      },
    ];
  });
}

/**
 * Why the lens found nowhere: a refusal that day leaves them out of every
 * clean fit, a full day leaves no piece, and otherwise their sheet and their
 * shifts simply meet no gap.
 */
export function lensEmptyReason(
  data: TimelinePayload,
  planning: ShiftPlanning,
  v: PoolVolunteer,
): 'declined' | 'full' | 'none' {
  if (declinedOnDay(v, data)) return 'declined';
  if (planning.maxDailyMin - v.dayMinutes < planning.minPieceMin) return 'full';
  return 'none';
}

/**
 * What dropping a person at `minute` on a post's row would do — the one answer
 * the drag preview draws and the drop carries out, so the two never disagree.
 *
 * `minute` is where the pointer is, unsnapped: a row off the quarter hour
 * (07:50–08:30) is hit where it is drawn. Only a piece is snapped.
 *
 *  - `same` — a row they already hold there: nothing to do.
 *  - `replace` — somebody else's row the board shows: asked as "namesto …".
 *  - `fill` — an uncovered stretch (`fillTargets`). An open row is taken
 *    whole, as autofill and the gap list take it; a gap no longer than the
 *    post type's longest shift too. A longer gap gets the recommended length
 *    centred on the pointer, kept inside the gap, run on to its edge rather
 *    than leave less than the shortest piece, and then kept off the person's
 *    own rows when the pointer is beside one rather than on it.
 *  - `create` — a post with no opening window that day, as a drop always did:
 *    three hours from the pointer.
 *  - `null` — a closed band, a finished row: nothing happens.
 *
 * `candidate` is the person ranked for the piece (`rankPiece`); `clash` is a
 * row of theirs it still runs into.
 */
export type DropPlan =
  | { kind: 'same'; shift: Shift }
  | { kind: 'replace'; shift: Shift; holder: PoolVolunteer | undefined }
  | {
      kind: 'fill';
      target: FillTarget;
      piece: MinuteRange;
      narrows: boolean;
      candidate: PieceCandidate | null;
      clash: DayShift | null;
    }
  | { kind: 'create'; piece: MinuteRange; candidate: PieceCandidate | null; clash: DayShift | null }
  | null;

export function dropPlan(
  input: AutofillInput,
  data: TimelinePayload,
  volunteerId: string,
  postId: string,
  minute: number,
  opts: {
    axisEnd: number;
    shown: { shift: (post: TimelinePost, s: Shift) => boolean; gap: (post: TimelinePost) => boolean };
  },
): DropPlan {
  const post = data.posts.find((p) => p.id === postId);
  if (!post) return null;
  const snap = (m: number) => Math.round(m / 15) * 15;

  const hit = post.shifts.find(
    (s) =>
      s.volunteerId !== null &&
      s.state !== 'done' &&
      s.fromMin <= minute &&
      minute < s.toMin &&
      opts.shown.shift(post, s),
  );
  if (hit) {
    if (hit.volunteerId === volunteerId) return { kind: 'same', shift: hit };
    return { kind: 'replace', shift: hit, holder: data.volunteers.find((v) => v.id === hit.volunteerId) };
  }

  const rank = (piece: MinuteRange) => ({
    candidate:
      rankPiece(input, { ...piece, postId, urgent: post.urgent }).find((c) => c.volunteer.id === volunteerId) ??
      null,
    clash: shiftClash(data, volunteerId, piece),
  });

  const target = fillTargets(data).find(
    (t) =>
      t.postId === postId &&
      t.fromMin <= minute &&
      minute < t.toMin &&
      (t.kind === 'open'
        ? opts.shown.shift(post, post.shifts.find((s) => s.id === t.shiftId)!)
        : opts.shown.gap(post)),
  );
  if (target) {
    const whole = { fromMin: target.fromMin, toMin: target.toMin };
    const { targetMin, maxMin } = input.planning.byType[post.type];
    const least = Math.min(input.planning.minPieceMin, whole.toMin - whole.fromMin);
    let piece = whole;
    if (target.kind === 'gap' && whole.toMin - whole.fromMin > maxMin) {
      const fromMin = Math.max(whole.fromMin, Math.min(snap(minute - targetMin / 2), whole.toMin - targetMin));
      piece = { fromMin, toMin: fromMin + targetMin };
      if (piece.fromMin - whole.fromMin < least && piece.toMin - whole.fromMin <= maxMin) {
        piece = { ...piece, fromMin: whole.fromMin };
      }
      if (whole.toMin - piece.toMin < least && whole.toMin - piece.fromMin <= maxMin) {
        piece = { ...piece, toMin: whole.toMin };
      }
    }
    if (target.kind === 'gap') {
      const own = personDayShifts(data, volunteerId);
      const beside = subtractRanges(piece, own).find((r) => r.fromMin <= minute && minute < r.toMin);
      if (beside && beside.toMin - beside.fromMin >= least) piece = { fromMin: beside.fromMin, toMin: beside.toMin };
    }
    return {
      kind: 'fill',
      target,
      piece,
      narrows: piece.fromMin !== whole.fromMin || piece.toMin !== whole.toMin,
      ...rank(piece),
    };
  }

  if (post.windows.length > 0) return null;
  const fromMin = snap(minute);
  const piece = { fromMin, toMin: Math.min(fromMin + 180, opts.axisEnd) };
  if (piece.toMin - piece.fromMin < MIN_SHIFT_MINUTES) return null;
  return { kind: 'create', piece, ...rank(piece) };
}

export interface ResizeBounds {
  fromFloor: number;
  fromCeil: number;
  toFloor: number;
  toCeil: number;
}

/**
 * How far each edge of a shift may be dragged.
 *
 * Two walls, and one escape hatch:
 *
 *  - the post's **opening window** — the izmena stays inside the Časovnica
 *    mesta it belongs to, so a drag can never run across a ZAPRTO band or past
 *    closing time. A shift the window no longer contains (Mesta replaces a
 *    day's windows without consulting existing shifts) falls back to a window
 *    it overlaps, then to the axis, rather than being frozen;
 *  - the **neighbouring izmene** on the same post. The edge stops dead at one
 *    rather than overlapping it, so a drag can never invalidate somebody
 *    else's schedule. Moving a handover is therefore two gestures: shrink one
 *    side, which opens a PROSTO, then grow the other into it. A shift that
 *    already clashes with this one is not a wall, so a pre-existing conflict
 *    cannot lock the drag out;
 *  - and whatever happens, the bounds contain the shift's **own** current
 *    times. Otherwise the first pixel of a drag would silently truncate a
 *    shift that starts outside its window, which is the same escape hatch
 *    `timeOptions` makes for a time outside the axis.
 */
export function resizeBounds(
  shift: { fromMin: number; toMin: number },
  windows: MinuteRange[],
  siblings: { fromMin: number; toMin: number }[],
  axis: { fromMin: number; toMin: number },
): ResizeBounds {
  const overlaps = (a: MinuteRange, b: MinuteRange) => a.fromMin < b.toMin && b.fromMin < a.toMin;
  const win =
    windows.find((w) => w.fromMin <= shift.fromMin && shift.toMin <= w.toMin) ??
    windows.find((w) => overlaps(w, shift)) ??
    axis;

  // A wall lies wholly to one side, which is also why a shift already
  // overlapping this one can never be one: it would have to both end before
  // this shift starts and reach past that point.
  const left = siblings.filter((s) => s.toMin <= shift.fromMin).map((s) => s.toMin);
  const right = siblings.filter((s) => s.fromMin >= shift.toMin).map((s) => s.fromMin);

  return {
    fromFloor: Math.min(shift.fromMin, Math.max(win.fromMin, ...left)),
    fromCeil: shift.toMin - MIN_SHIFT_MINUTES,
    toFloor: shift.fromMin + MIN_SHIFT_MINUTES,
    toCeil: Math.max(shift.toMin, Math.min(win.toMin, ...right)),
  };
}

export interface DayGroup {
  eventDayId: string;
  dayLabel: string;
  /** That day's total, for the group heading. */
  minutes: number;
  shifts: VolunteerShift[];
}

/**
 * One person's shifts as the hover card lists them: a group per event day,
 * carrying that day's total. `rollupFor` hands them over sorted by day and then
 * by start, so a single pass is enough and the day order it decided survives.
 */
export function groupShiftsByDay(shifts: VolunteerShift[]): DayGroup[] {
  const groups: DayGroup[] = [];
  for (const s of shifts) {
    let last = groups.at(-1);
    if (!last || last.eventDayId !== s.eventDayId) {
      last = { eventDayId: s.eventDayId, dayLabel: s.dayLabel, minutes: 0, shifts: [] };
      groups.push(last);
    }
    last.shifts.push(s);
    last.minutes += s.toMin - s.fromMin;
  }
  return groups;
}

/** Row coverage % for the sticky cell (assigned window time / window time). */
export function rowCoverage(p: TimelinePost): number | null {
  const winMin = p.windows.reduce((a, w) => a + (w.toMin - w.fromMin), 0);
  if (winMin === 0) return null;
  const covered = p.shifts.filter(staffs);
  const gapMin = computeGaps(p.windows, covered).reduce((a, g) => a + (g.toMin - g.fromMin), 0);
  return Math.round(((winMin - gapMin) / winMin) * 100);
}
